/**
 * App-level reaction holding every rule that ends a speech feature: Voice
 * Mode stops when speech access is lost, when its destination chat is gone,
 * or - audibly - when send-blocking limits are hit or its owner chat enters
 * the errored (retry) state; Read Aloud stops when access is lost or its
 * owner chat is deleted or missing. Voice Mode machine ownership stays in
 * the SDK.
 */
import { ChatStatus } from '@littlebird/sdk';
import { comparer, reaction } from 'mobx';

import { isOutOfCreditsBlocking } from '@/features/subscription';
import { littlebirdSdk } from '@/lib/sdk-init';

import { readAloudStore } from './read-aloud.store';
import { canUseSpeechFeatures } from './speech-features';
import { isVoiceDestinationUsable } from './voice-destination';

/**
 * Observe speech access and owner lifecycles for the authenticated app shell.
 * The returned disposer stops the MobX reaction but does not itself stop audio.
 */
export function watchSpeechLifecycle(): () => void {
  return reaction(
    () => {
      const ownerThreadId = readAloudStore.currentOwnerThreadId;
      const ownerThread = ownerThreadId
        ? littlebirdSdk.stores.threads.getById(ownerThreadId)
        : undefined;
      const session = littlebirdSdk.voiceMode.session;
      const sessionDestinationUnavailable = Boolean(
        session && !isVoiceDestinationUsable(session.draftId)
      );
      const sessionThread = session
        ? littlebirdSdk.stores.threads.getById(session.draftId)
        : undefined;
      return {
        // The user: may they use speech features at all? Says nothing about any chat
        canUse: canUseSpeechFeatures(),
        session,
        // The chat's existence: does the place this session sends to still exist?
        sessionDestinationUnavailable,
        // The account: can it send any message right now (credits)?
        // Checked here rather than in the chat input (`Composer`), which is
        // unmounted off chat pages and only knows its own draft, so it could
        // not stop a session owned by a different chat
        sendBlocked: isOutOfCreditsBlocking(),
        // The chat's state: it exists but is blocked behind the retry button
        // until a retry clears the error, so no session may run in it
        ownerChatErrored: sessionThread?.status === ChatStatus.Errored,
        ownerDeleted: Boolean(
          ownerThreadId && (!ownerThread || ownerThread.deletedAt)
        ),
      };
    },
    // Read Aloud stops only when access flips from on to off. Voice Mode below
    // checks the current value instead, so even a session started while access
    // was already absent is stopped
    (
      {
        canUse,
        session,
        sessionDestinationUnavailable,
        sendBlocked,
        ownerChatErrored,
        ownerDeleted,
      },
      previous
    ) => {
      const lostAccess = !canUse && previous?.canUse !== false;
      if (ownerDeleted || lostAccess) readAloudStore.stopCurrentRequest();
      if (!session) return;
      // The stops for send limits and an errored chat are audible: they arrive
      // mid-conversation through no action the user just took. The rest stay
      // silent: turning speech off is the user's own action, and a chat
      // deleted elsewhere is cued by the sync code that removes it (thread-store)
      if (sendBlocked || ownerChatErrored) {
        littlebirdSdk.voiceMode.stopSessionWithHangupCue(session.draftId);
      } else if (!canUse || sessionDestinationUnavailable) {
        littlebirdSdk.voiceMode.stopSession(session.draftId);
      }
    },
    { equals: comparer.shallow, fireImmediately: true }
  );
}
